import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { IconX, IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react'
import classNames from 'classnames'
import queryString from 'query-string'
import { isFunction } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { usePortal } from '@/utils/hooks/usePortal'
import { useEscapeKey } from '@/utils/hooks/useEscapeKey'

import './Drawer.scss'

// Single source of truth for the open/close timing: drives both the unmount
// timer below and the CSS animation duration (via --drawer-animation-duration).
const DRAWER_ANIMATION_MS = 200
const drawerAnimationStyle = { '--drawer-animation-duration': `${DRAWER_ANIMATION_MS}ms` }

/**
 * @import import { Drawer } from '@/components/drawer/Drawer'
 * @param {object} props
 * @param {string|number} [props.id] Written to the URL as `?id=<id>` for deep linking when syncUrl is on
 * @param {node} [props.title] Title bar content; the bar also holds the expand and close buttons
 * @param {Function} [props.trigger] Render prop `(open) => node` rendering the element that opens the drawer
 * @param {boolean} [props.wide] 650px panel instead of the default 480px
 * @param {boolean} [props.extraWide] 850px panel
 * @param {boolean} [props.expandable] Adds a title-bar button that expands the drawer to the full viewport width beside the sidebar
 * @param {node|Function} props.children Drawer content; a function receives `(close, { expanded })`
 * @param {boolean} [props.autoTrigger] Opens the drawer on mount
 * @param {Function} [props.onClose] Called when the drawer closes
 * @param {boolean} [props.warnOnClose] Confirms unsaved changes in a modal before closing
 * @param {boolean} [props.syncUrl] Set false to keep the drawer out of the URL query string
 */
export const Drawer = ({
  id,
  title,
  trigger,
  wide,
  extraWide,
  expandable = false,
  children,
  autoTrigger = false,
  onClose = () => {},
  warnOnClose = false,
  // Drawers are deep-linkable by default: opening writes `?id=<id>` and closing
  // clears the search. Pages that own their own query string (e.g. Notetaker
  // mirrors its org/channel/playbook selection there) opt out, so opening a
  // drawer can't wipe those params.
  syncUrl = true,
}) => {
  const portal = usePortal()
  const history = useHistory()
  const location = useLocation()
  const [open, setOpen] = useState(autoTrigger)
  const [expanded, setExpanded] = useState(false)
  const [showCloseWarning, setShowCloseWarning] = useState(false)
  // Keep the drawer mounted while it animates out, then unmount on animation end
  const [rendered, setRendered] = useState(autoTrigger)
  const [closing, setClosing] = useState(false)
  const { search } = location
  const parsed = queryString.parse(search)

  const handleOpen = () => {
    if (syncUrl) {
      history.push({ search: queryString.stringify({ id }) })
    }
    setOpen(true)
  }

  const handleClose = () => {
    if (syncUrl) {
      history.push({ search: queryString.stringify({}) })
    }
    setOpen(false)
    onClose()
  }

  const attemptClose = () => {
    if (warnOnClose) {
      setShowCloseWarning(true)
    } else {
      handleClose()
    }
  }

  useEscapeKey(attemptClose, open && !showCloseWarning)

  // Mount immediately on open; on close, play the slide-out animation and then
  // unmount once it finishes. The 200ms mirrors the 0.2s animation in Drawer.scss.
  useEffect(() => {
    if (open) {
      setRendered(true)
      setClosing(false)
      return undefined
    }

    if (!rendered) {
      return undefined
    }

    setClosing(true)
    const timer = setTimeout(() => {
      setClosing(false)
      setRendered(false)
    }, DRAWER_ANIMATION_MS)

    return () => clearTimeout(timer)
  }, [open, rendered])

  useEffect(() => {
    // Only sync with URL if this drawer has an id
    if (syncUrl && id) {
      // eslint-disable-next-line eqeqeq
      const isCurrentDrawer = parsed.id == id

      if (isCurrentDrawer && !open) {
        setOpen(true)
      }
    }
  }, [open, id, parsed, syncUrl])

  return (
    <>
      {isFunction(trigger) && trigger(handleOpen)}

      {(open || rendered) &&
        portal(
          <>
            <div
              className={classNames('drawer-overlay', { closing })}
              style={drawerAnimationStyle}
              onClick={attemptClose}
              data-testid="drawer-overlay"
            />
            <aside
              className={classNames('drawer', { wide, 'extra-wide': extraWide, expanded, closing })}
              style={drawerAnimationStyle}
              role="dialog"
              aria-modal="true"
              data-testid="drawer"
            >
              {title && (
                <div className="drawer-title">
                  {title}
                  <div className="flex-align-center small-gap">
                    {expandable && (
                      <Button
                        square
                        small
                        basic
                        onClick={() => setExpanded((current) => !current)}
                        data-testid="drawer-expand-button"
                      >
                        {expanded ? <IconArrowsMinimize /> : <IconArrowsMaximize />}
                      </Button>
                    )}
                    <Button
                      square
                      small
                      basic
                      onClick={attemptClose}
                      data-testid="drawer-close-button"
                    >
                      <IconX />
                    </Button>
                  </div>
                </div>
              )}
              <div className="drawer-content">
                {isFunction(children) ? children(handleClose, { expanded }) : children}
              </div>
            </aside>
          </>
        )}

      <BasicModal
        title="Unsaved changes"
        show={showCloseWarning}
        onClose={() => setShowCloseWarning(false)}
        size="tiny"
        footer={
          <div className="flex-align-center flex-end small-gap">
            <Button basic onClick={() => setShowCloseWarning(false)}>
              Keep editing
            </Button>
            <Button
              critical
              onClick={() => {
                setShowCloseWarning(false)
                handleClose()
              }}
              data-testid="drawer-discard-changes-button"
            >
              Discard changes
            </Button>
          </div>
        }
      >
        <p>You have unsaved changes that will be lost.</p>
      </BasicModal>
    </>
  )
}
